import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, EmptyState, Toggle } from '@teals/ui-web';
import { useAuth } from '../../hooks/useAuth';
import { useOrgRole } from '../../hooks/useOrgRole';
import { useGetOrgModelQuery, useGetOrgModelsQuery, type OrgModel } from '../../store/api/orgModelsApi';
import { labLabel, modelMetaLine, routeLabel } from '../../utils/modelDisplay';
import {
  canManageModels,
  canTryModels,
  costTierDots,
  costTierLabel,
  createAgentPath,
  formatTokens,
} from './modelsShared';
import {
  ActionBar,
  BackLink,
  CostTierText,
  Header,
  HeaderCopy,
  Meta,
  Muted,
  Page,
  Section,
  SectionTitle,
  Title,
} from './modelsStyles';
import { useModelToggle } from './useModelToggle';

const Specs = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 1rem;
  margin: 0;

  dt {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  dd {
    margin: 0.125rem 0 0;
    font-size: 0.9375rem;
    color: var(--text-primary);
  }
`;

const BenchTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    text-align: left;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--border);
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
  }
`;

const Plain = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.6;
`;

const LOCATION_LABEL: Record<string, string> = {
  united_states: 'United States',
  australia: 'Australia',
  asia_pacific: 'Asia Pacific',
  global: 'Global',
};

function liveDataLabel(features?: string[]): string {
  const names: string[] = [];
  if (features?.some((feature) => feature === 'live_web' || feature === 'live_web_search')) {
    names.push('Web');
  }
  if (features?.some((feature) => feature === 'live_x' || feature === 'live_x_search')) {
    names.push('X');
  }
  return names.length ? names.join(' + ') : 'None';
}

function locationLabel(value?: string | null): string {
  if (!value) return '—';
  return LOCATION_LABEL[value] || value;
}

const WayRow = styled.tr<{ $current?: boolean }>`
  background: ${({ $current }) => ($current ? 'var(--surface-muted, rgba(0,0,0,0.03))' : 'transparent')};
`;

const Banner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: var(--warning-bg, #fff7e6);
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const ModelDetail: React.FC = () => {
  const { productId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { role } = useOrgRole();
  const canManage = canManageModels(role);
  const canTry = canTryModels(role);
  const { data: model, isLoading, isError } = useGetOrgModelQuery(
    { orgId: orgId || '', productId },
    { skip: !orgId || !productId },
  );
  const { data: catalog = [] } = useGetOrgModelsQuery(orgId || '', { skip: !orgId });
  const { toggle, patching, disableModal } = useModelToggle(orgId);

  if (!orgId) return null;

  if (isLoading) {
    return (
      <Page>
        <Muted>Loading model…</Muted>
      </Page>
    );
  }

  if (isError || !model) {
    return (
      <Page>
        <EmptyState
          title="Model not found"
          description="It may have been removed from the catalog."
          action={<Button onClick={() => navigate('/models')}>Back to Models</Button>}
        />
      </Page>
    );
  }

  const chat = model.chatCapable !== false;
  const modelKey = model.modelKey || model.productId;
  const ways = catalog.filter((way) => (way.modelKey || way.productId) === modelKey);
  const wayRows: OrgModel[] = ways.length ? ways : [model];
  const retiring = model.lifecycle?.stage === 'retiring' || model.lifecycle?.stage === 'retired';
  const agentsUsing = model.usedBy.agents.length;
  const defaultAgents = model.usedBy.agents
    .filter((a) => a.role === 'default')
    .map((a) => a.name);

  return (
    <Page>
      <BackLink type="button" onClick={() => navigate('/models')}>
        ← Models
      </BackLink>

      <Header>
        <HeaderCopy>
          <Title>{model.displayName}</Title>
          <Meta>{modelMetaLine(model)}</Meta>
          <div>
            <Badge variant={model.enabled ? 'success' : 'neutral'} size="sm">
              {model.enabled ? 'On for this organisation' : 'Off for this organisation'}
            </Badge>
            {!chat && (
              <Badge variant="neutral" size="sm" style={{ marginLeft: '0.5rem' }}>
                Not for chat
              </Badge>
            )}
          </div>
        </HeaderCopy>
        <ActionBar>
          {canManage && (
            <Toggle
              value={model.enabled}
              onChange={() => void toggle(model, defaultAgents)}
              disabled={patching}
              label={model.enabled ? 'On' : 'Off'}
            />
          )}
          {chat && canTry && (
            <Button
              variant="outline"
              onClick={() => navigate(createAgentPath(model.productId))}
            >
              Create agent with this model
            </Button>
          )}
          {chat && canTry && (
            <Button
              variant="primary"
              onClick={() =>
                navigate(`/models/${encodeURIComponent(model.productId)}/playground`)
              }
            >
              Try in playground
            </Button>
          )}
        </ActionBar>
      </Header>

      {retiring && (
        <Banner role="status">
          {model.lifecycle?.stage === 'retired' ? 'This way to run is retired.' : 'This way to run is retiring'}
          {model.lifecycle?.retiresOn ? ` on ${model.lifecycle.retiresOn}` : ''}.
          {agentsUsing > 0
            ? ` ${agentsUsing} agent${agentsUsing === 1 ? '' : 's'} use this.`
            : ''}{' '}
          <button
            type="button"
            onClick={() =>
              navigate(
                `/models?advisor=open&q=${encodeURIComponent(`replacement for ${model.displayName}`)}`,
              )
            }
          >
            Find a replacement
          </button>
        </Banner>
      )}

      <Section>
        <Header>
          <SectionTitle>Ways to run</SectionTitle>
          {wayRows.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/models/compare?ids=${wayRows.map((way) => way.productId).join(',')}`)
              }
            >
              Compare ways
            </Button>
          )}
        </Header>
        <BenchTable>
          <thead>
            <tr>
              <th>Way to run</th>
              <th>Data location</th>
              <th>Live data</th>
              <th>Cost</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {wayRows.map((way) => (
              <WayRow key={way.productId} $current={way.productId === model.productId}>
                <td>
                  {way.wayLabel || way.productId}
                  <Meta> {way.productId}</Meta>
                  {way.lifecycle?.stage === 'retiring' && way.lifecycle.retiresOn
                    ? ` · Retires ${way.lifecycle.retiresOn}`
                    : ''}
                </td>
                <td>{locationLabel(way.dataLocation)}</td>
                <td>{liveDataLabel(way.features)}</td>
                <td>
                  {costTierLabel(way.costTier)} {costTierDots(way.costTier)}
                </td>
                <td>
                  {canManage ? (
                    <Toggle
                      value={way.enabled}
                      onChange={() => void toggle(way, way.productId === model.productId ? defaultAgents : [])}
                      disabled={patching || way.lifecycle?.stage === 'retired'}
                      label={way.enabled ? 'On' : 'Off'}
                    />
                  ) : (
                    <Badge variant={way.enabled ? 'success' : 'neutral'} size="sm">
                      {way.enabled ? 'On' : 'Off'}
                    </Badge>
                  )}
                </td>
                <td>
                  {canTry && way.chatCapable !== false && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/models/${encodeURIComponent(way.productId)}/playground`)}
                    >
                      Try
                    </Button>
                  )}
                </td>
              </WayRow>
            ))}
          </tbody>
        </BenchTable>
        {model.searchNote && <Muted>{model.searchNote}</Muted>}
        <Muted>
          Speed:{' '}
          {model.speed?.measured && model.speed.p50TtftMs != null
            ? `median first token ${model.speed.p50TtftMs} ms, measured on Luminafactory, last ${model.speed.windowDays || 7} days`
            : '—'}
        </Muted>
      </Section>

      {model.bestFor && model.bestFor.length > 0 && (
        <Section>
          <SectionTitle>Best for</SectionTitle>
          <Plain>
            {model.bestFor.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </Plain>
        </Section>
      )}

      <Card padding={20} elevation="sm">
        <Specs>
          <div>
            <dt>Relative cost</dt>
            <dd>
              <CostTierText>
                {costTierLabel(model.costTier)}
                <span aria-hidden>{costTierDots(model.costTier)}</span>
              </CostTierText>
            </dd>
          </div>
          <div>
            <dt>Context window</dt>
            <dd>{formatTokens(model.contextWindow)} tokens</dd>
          </div>
          <div>
            <dt>Max output</dt>
            <dd>
              {model.maxOutputTokens ? `${formatTokens(model.maxOutputTokens)} tokens` : '—'}
            </dd>
          </div>
          <div>
            <dt>Lab</dt>
            <dd>{labLabel(model.lab) || '—'}</dd>
          </div>
          <div>
            <dt>Runs on</dt>
            <dd>{routeLabel(model.route, model.provider) || '—'}</dd>
          </div>
          <div>
            <dt>Capabilities</dt>
            <dd>{model.capabilities.length ? model.capabilities.join(', ') : '—'}</dd>
          </div>
        </Specs>
      </Card>

      <Section>
        <SectionTitle>Benchmarks</SectionTitle>
        {model.benchmarks && model.benchmarks.length > 0 ? (
          <>
            <BenchTable>
              <thead>
                <tr>
                  <th>Measures</th>
                  <th>Benchmark</th>
                  <th>Score</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {model.benchmarks.map((b) => (
                  <tr key={b.key}>
                    <td>{b.label}</td>
                    <td>{b.name}</td>
                    <td>{b.score}%</td>
                    <td>
                      <a href={b.sourceUrl} target="_blank" rel="noreferrer">
                        Lab report
                      </a>{' '}
                      <Meta as="span" style={{ display: 'inline' }}>
                        · {b.asOf}
                      </Meta>
                    </td>
                  </tr>
                ))}
              </tbody>
            </BenchTable>
            <Muted>Scores are reported by the lab, not measured by Lumina.</Muted>
          </>
        ) : (
          <Muted>Not yet benchmarked.</Muted>
        )}
      </Section>

      <Section>
        <SectionTitle>Used in this organisation</SectionTitle>
        {model.usedBy.agents.length === 0 && model.usedBy.applications.length === 0 ? (
          <Muted>No agents or applications use this model yet.</Muted>
        ) : (
          <Plain>
            {model.usedBy.agents.map((a) => (
              <li key={a.agentId}>
                <Link to={`/agents/${encodeURIComponent(a.agentId)}`}>{a.name}</Link>{' '}
                — {a.role === 'default' ? 'default model' : 'recommended'}
              </li>
            ))}
            {model.usedBy.applications.map((app) => (
              <li key={app.applicationId}>
                <Link to={`/applications/${encodeURIComponent(app.applicationId)}`}>
                  {app.name}
                </Link>{' '}
                — application allows it
              </li>
            ))}
          </Plain>
        )}
      </Section>

      {disableModal}
    </Page>
  );
};

export default ModelDetail;
